import { formatDateTime } from "@/v2-common/utils/utils.date"
import {
  formatNumber,
  truncateString,
} from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { useAgentConversationViewQuery } from "@/v2-backoffice-client/agentConversation/view/agentConversation.view.query"
import type { AgentConversationViewQuery } from "@/v2-backoffice-client/agentConversation/view/__types__/agentConversation.view.query"

type AgentTurn = AgentConversationViewQuery["getAgentConversationTurns"][number]
type AgentToolCall = AgentTurn["ToolCalls"][number]

function AgentConversationToolCall(props: { toolCall: AgentToolCall }) {
  const { toolCall } = props

  return (
    <li
      className={cn(
        "rounded-md border border-border p-2 text-xs",
        toolCall.IsError && "border-destructive/50",
      )}
      data-slot="agent-conversation-tool-call"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">
          {toolCall.ServerName} · {toolCall.ToolName}
        </span>
        <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
          <span>{toolCall.Source}</span>
          {toolCall.IsError && (
            <span className="font-medium text-destructive">Error</span>
          )}
        </span>
      </div>
      {toolCall.Arguments && (
        <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-[11px]">
          {toolCall.Arguments}
        </pre>
      )}
    </li>
  )
}

function AgentConversationTurn(props: { turn: AgentTurn }) {
  const { turn } = props

  return (
    <li
      className="rounded-lg border border-border p-3"
      data-slot="agent-conversation-turn"
    >
      <div className="text-xs font-medium text-muted-foreground">
        Turn {turn.Seq} · {formatDateTime(new Date(turn.StartedAt))}
      </div>

      <p className="mt-1.5 whitespace-pre-wrap text-sm">{turn.Prompt}</p>

      {turn.Answer && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
          {turn.Answer}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {turn.Model && <span>{turn.Model}</span>}
        <span>
          {formatNumber(Number(turn.InputTokens))} in /{" "}
          {formatNumber(Number(turn.OutputTokens))} out
        </span>
        <span>{turn.TotalMs} ms</span>
        {turn.TimeToFirstTextMs != null && (
          <span>{turn.TimeToFirstTextMs} ms to first text</span>
        )}
        <span>{turn.StopReason}</span>
      </div>

      {turn.Context && (
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-[11px]">
          {turn.Context}
        </pre>
      )}

      {turn.ToolCalls.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5" role="list">
          {turn.ToolCalls.map((toolCall) => (
            <AgentConversationToolCall key={toolCall.Id} toolCall={toolCall} />
          ))}
        </ul>
      )}
    </li>
  )
}

function AgentConversationViewSheet() {
  const { id } = useAppViewSheetOpener()
  const conversationId = id ?? ""

  const { data, loading, error } = useAgentConversationViewQuery({
    skip: !conversationId,
    variables: { conversationId },
  })

  const turns = data?.getAgentConversationTurns ?? []
  const firstTurn = turns[0]

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading}>
          {firstTurn ? truncateString(firstTurn.Prompt, 60) : "Conversation"}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <div className="flex-1 min-h-0 overflow-auto p-4">
        {error && (
          <p className="text-sm text-destructive">
            Failed to load the conversation.
          </p>
        )}
        {!loading && !error && turns.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No turns recorded yet.
          </p>
        )}
        <ul className="flex flex-col gap-3" role="list">
          {turns.map((turn) => (
            <AgentConversationTurn key={turn.Id} turn={turn} />
          ))}
        </ul>
      </div>
    </>
  )
}

export default AgentConversationViewSheet
